<template lang="pug">

    .object-three(:class="`object-three--${realResize} ${typeClass}`", v-inview:class="['entered']")


        .object-three__wrap(v-if='object.image.src', :style='{justifyContent: alignH, alignItems: alignV}')
            .object-three__size-container(:style="style")
                .object-three__point
            .object-three__decoration
                decoration(v-if='object && object.decoration', :count='object.decoration.count', :active='active')
                app-image.object-three__img(v-if="!hasWebGL", :lazy-src="src", :lazy-srcset="srcset", :alt="object.alt", :class="imgClass")
                app-image.object-three__img.object-three__img--table(v-if="hasTable", :lazy-src="tblSrc", :lazy-srcset="tblSrcset", :alt="object.alt", :class="imgClass", :style="tblStyle")

</template>

<script>
/* Components */
import AppImage from '@/components/AppImage.vue';
import Decoration from '@/components/illustrations/Decoration.vue';

import * as basicScroll from 'basicscroll';
import {TweenLite} from 'gsap';
import {mapGetters} from 'vuex';

// const containerWidth = 1220;

export default {
    name: 'object-three',
    props: {
        object: {
            type: Object,
            default: null
        },

        base: {
            type: String,
            default: ''
        },

        resize: {
            type: String,
            default: ''
        },
        active: {
            default: false,
            type: Boolean
        }
    },

    components: {
        AppImage,
        Decoration
    },

    computed: {
        ...mapGetters('decor3d', ['hasWebGL']),

        src() {
            return this.base + (this.object.image.src || '');
        },

        width() {
            return this.object.image.width || 442;
        },

        height() {
            return this.object.image.height || 616;
        },

        left() {
            return this.object.image.left || 0;
        },

        top() {
            return this.object.image.top || 0;
        },

        index() {
            return this.object.index || 0;
        },

        alignH() {
            let value = '';

            switch (this.object.image.alignH) {
                case 'left':
                    value = 'flex-start';
                    break;
                case 'center':
                    value = 'center';
                    break;
                case 'right':
                    value = 'flex-end';
                    break;
                default:
                    value = 'flex-start';
                    break;
            }
            return value;
        },

        alignV() {
            let value = '';

            switch (this.object.image.alignV) {
                case 'top':
                    value = 'flex-start';
                    break;
                case 'center':
                    value = 'center';
                    break;
                case 'bottom':
                    value = 'flex-end';
                    break;
                default:
                    value = 'flex-start';
                    break;
            }
            return value;
        },

        realResize() {
            return this.object.image.customResize || this.resize;
        },

        typeClass() {
            if (!this.object.image.types || !(this.object.image.types instanceof Array)) return '';
            return 'object-three--' + this.object.image.types.join(' object-three--');
        },

        imgClass() {
            if (!this.width || !this.height) return '';
            return 'aspect-ratio';
        },

        srcCompact() {
            return this.object.image.srcCompact;
        },

        srcset() {
            if (!this.srcCompact) return null;
            return `${this.src} 1400w, ${this.base}${this.srcCompact} 800w`;
        },

        hasTable() {
            return !!this.object.table;
        },

        table() {
            return this.object.table;
        },

        tblSrc() {
            return this.base + (this.table.src || '');
        },

        tblSrcCompact() {
            return this.table.srcCompact;
        },

        tblSrcset() {
            if (!this.tblSrcCompact) return null;
            return `${this.tblSrc} 1400w, ${this.base}${this.tblSrcCompact} 800w`;
        },

        aspectRatio() {
            // Calculate the aspect ratio of the image
            // if the width and the height are given.
            if (!this.width || !this.height) return null;
            return (this.height / this.width) * 100;
        },

        style() {
            const style = {height: '100%'};
            if (!(this.object.image.width || this.object.image.height)) return {width: '100%', height: '100%'};

            if (this.aspectRatio) {
                // style.width = `${(this.width / containerWidth) * 100}%`;
                style.width = '100%';
                style.height = 0;
                style.paddingBottom = `${this.aspectRatio}%`;
                style.left = `${this.left}%`;
                style.top = `${this.top}%`;
            }

            return style;
        },

        tblStyle() {
            if (!this.table) return {};
            const style = {};
            if (this.table.left) style.left = `${this.table.left}%`;
            if (this.table.top) style.top = `${this.table.top}%`;
            return style;
        }
    },

    mounted() {
        this.stopBasicscroll();
        TweenLite.delayedCall(0.15, this.startBasicscroll, [], this);
    },

    beforeDestroy() {
        window.removeEventListener('updateObjectsList', this.basicScrollUpdate);
        TweenLite.killDelayedCallsTo(this.startBasicscroll);
        TweenLite.killDelayedCallsTo(this.basicScrollUpdate);
        this.stopBasicscroll();
    },

    methods: {
        setProgress(val) {
            if (val === this.progress) return;
            this.progress = val;
            this.$store.dispatch('decor3d/setObjectProgress', {value: val, index: this.index});
        },

        startBasicscroll() {
            if (this._hasBSC) return;

            let instance = basicScroll.create({
                elem: this.$el.querySelector('.object-three__size-container'),
                from: 'bottom-top',
                to: 'top-bottom',
                inside: (instance, percentage) => {
                    // console.log(this.$el, 1 - percentage / 100)
                    this.setProgress(1 - percentage / 100);
                },
                outside: (instance, percentage) => {
                    // console.log(instance, 1 - percentage / 100)
                    this.setProgress(1 - percentage / 100);
                }
            });
            instance.start();
            this._BSC = instance;

            if (this.hasTable) {
                instance = basicScroll.create({
                    elem: this.$el.querySelector('.object-three__decoration'),
                    from: 'top-bottom',
                    to: 'middle-middle',
                    direct: true,
                    props: {
                        '--ratio': {
                            from: 1,
                            to: 0
                        }
                    }
                });
                instance.start();
                this._BSC2 = instance;
            }

            this.bsupdated = true;

            TweenLite.delayedCall(0.1, this.basicScrollUpdate, [], this);

            window.addEventListener('updateObjectsList', this.basicScrollUpdate);

            this._hasBSC = true;
        },

        stopBasicscroll() {
            if (!this._hasBSC) return;

            window.removeEventListener('scroll', this._basicScrollUpdate);
            window.removeEventListener('updateObjectsList', this.basicScrollUpdate);
            TweenLite.killDelayedCallsTo(this.basicScrollUpdate);
            this._BSC.destroy();

            this._BSC = null;
            this._hasBSC = false;
        },

        basicScrollUpdate() {
            if (!this.bsupdated) return;
            this.bsupdated = false;
            this._basicScrollUpdate();
            this.bsupdated = false;

            window.addEventListener('scroll', this._basicScrollUpdate);
        },

        _basicScrollUpdate() {
            if (this.bsupdated) return;
            if (!this._BSC) return;
            this._BSC.update();
            this._BSC.calculate();
            window.removeEventListener('scroll', this._basicScrollUpdate);
            this.bsupdated = true;
        }
    }
};
</script>

<style lang="scss">
//
@import '../../assets/css/vars';
//
.object-three {
    width: 100%;
    height: 100%;
    position: relative;

    @media (--viewport-mobile), (--viewport-tablet-pt) {
        min-height: 55vw;
        display: flex;
        align-items: center;
    }

    &__wrap {
        position: relative;
        width: 100%;
        height: 100%;
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        line-height: 0;

        @media (--viewport-mobile), (--viewport-tablet-pt) {
            height: auto !important;
            padding-bottom: 0 !important;
        }
    }

    .app-image {
        @media (--viewport-mobile), (--viewport-tablet-pt) {
            max-height: unset;
            width: 100%;
        }

        &.aspect-ratio {
            position: absolute;
            top: 0;
            left: 0;

            /*@media (--viewport-mobile), (--viewport-tablet-pt) {
                position: relative;
                top: auto;
                left: auto;
                transform: none;
            }*/
        }
    }

    // RESIZE
    &--contain {
        width: 100%;
        height: 100%;
    }

    // Enter animation
    &--alpha {
        transition: opacity 0.3s ease 0.15s;

        &:not(.entered) {
            opacity: 0;
        }
    }

    &__decoration {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        svg {
            transform: scale(0.5);
        }
    }

    &__size-container {
        position: relative;
        // background: rgba(255, 0, 0, 0.34);

        @media (--viewport-mobile), (--viewport-tablet-pt) {
            width: 100% !important;
            left: 0 !important;
            top: 0 !important;
            // PLACEHOLDER FOR Active area
            // height: auto !important;
            // padding-bottom: 0 !important;
        }
    }

    &__point {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 1px;
        height: 1px;
        // background: red;
    }

    &__img {
        position: absolute;
        top: 0;
        left: 0;

        @media (--viewport-mobile), (--viewport-tablet-pt) {
            padding: 0 var(--width-column-grid);
        }

        &--table {
            top: 50%;
            transform-origin: 50% 0;
            transform: scale(calc(1.125 * (0.8 + 1 * var(--ratio))), calc((1 + 0.5 * var(--ratio)) * 1.125 * (0.75 + 1 * var(--ratio)))) translate3d(0, calc(70% + 20% * var(--ratio)), 0);
            transition: transform 0.15s ease;
        }
    }
}
</style>
